<template>
  <div class="gallery-page">
    <transition name="loader">
      <div v-if="isLoading" class="loader-screen">
        <logo-loader />
      </div>
    </transition>

    <h1 class="gallery-title" :class="{ 'is-ready': !isLoading }">GALLERY</h1>

    <div class="gallery-grid" :class="{ 'is-ready': !isLoading }">
      <div v-for="(column, columnIndex) in columns" :key="columnIndex" class="gallery-column">
        <figure
          v-for="{ photo, index } in column"
          :key="photo.id"
          class="gallery-item"
          :style="{ '--delay': `${Math.min(index, 8) * 0.07}s` }"
        >
          <img
            :ref="(el) => (imageRefs[index] = el)"
            :class="{ 'is-loaded': loaded[photo.id] }"
            :src="photo.src"
            :srcset="photo.srcset"
            :sizes="sizes"
            :width="photo.width"
            :height="photo.height"
            :alt="photo.alt"
            :loading="index < 6 ? 'eager' : 'lazy'"
            :fetchpriority="index < 3 ? 'high' : 'auto'"
            decoding="async"
            @load="markLoaded(photo.id)"
            @error="markLoaded(photo.id)"
          />
        </figure>
      </div>
    </div>
  </div>
</template>

<script>
import LogoLoader from "./LogoLoader.vue";
import manifest from "@/assets/gallery/manifest.json";

// Generated by `npm run gallery` from the original photos in src/assets/gallery
const galleryImages = require.context("@/assets/gallery", false, /\.webp$/);

const LOADER_DURATION = 2000;

// Matches the breakpoints in the styles below: 1 column on phones, 2 on tablets, 3 on desktop
const TWO_COLUMNS = "(max-width: 899px)";
const ONE_COLUMN = "(max-width: 559px)";

export default {
  name: "GalleryPage",
  components: {
    LogoLoader,
  },
  data() {
    return {
      isLoading: true,
      columnCount: 3,
      loaded: {},
      imageRefs: [],
      // Matches the column widths below: 1 column on phones, 2 on tablets, 3 on desktop
      sizes: "(max-width: 559px) 100vw, (max-width: 899px) 50vw, 33vw",
      photos: manifest.map((photo) => ({
        ...photo,
        src: galleryImages(`./${photo.files[600]}`),
        srcset: Object.entries(photo.files)
          .map(([width, file]) => `${galleryImages(`./${file}`)} ${width}w`)
          .join(", "),
        alt: "Headshot by Studio Butter",
      })),
    };
  },
  computed: {
    // Deal photos across the columns in turn so the numbers read left to right, row by row
    columns() {
      const columns = Array.from({ length: this.columnCount }, () => []);
      this.photos.forEach((photo, index) => columns[index % this.columnCount].push({ photo, index }));
      return columns;
    },
  },
  methods: {
    markLoaded(id) {
      this.loaded[id] = true;
    },
    updateColumnCount() {
      this.columnCount = this.oneColumn.matches ? 1 : this.twoColumns.matches ? 2 : 3;
    },
  },
  created() {
    this.twoColumns = window.matchMedia(TWO_COLUMNS);
    this.oneColumn = window.matchMedia(ONE_COLUMN);
    this.updateColumnCount();
    this.twoColumns.addEventListener("change", this.updateColumnCount);
    this.oneColumn.addEventListener("change", this.updateColumnCount);
  },
  mounted() {
    // Images already in the browser cache can finish before @load is attached
    this.imageRefs.forEach((img, index) => {
      if (img && img.complete && img.naturalWidth) this.markLoaded(this.photos[index].id);
    });

    // The loader always shows for the full duration, even when everything is cached
    this.loaderTimer = setTimeout(() => {
      this.isLoading = false;
    }, LOADER_DURATION);
  },
  beforeUnmount() {
    clearTimeout(this.loaderTimer);
    this.twoColumns.removeEventListener("change", this.updateColumnCount);
    this.oneColumn.removeEventListener("change", this.updateColumnCount);
  },
};
</script>

<style scoped>
.gallery-page {
  max-width: 1440px;
  margin: 0 auto;
  padding: 56px 40px 140px;
  color: var(--color-ink);
}

/* Loader covers the page below the nav, then fades away */
.loader-screen {
  position: fixed;
  inset: var(--nav-height) 0 0 0;
  z-index: 900;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--color-paper);
}

.loader-leave-active {
  transition: opacity 0.6s ease;
}

.loader-leave-to {
  opacity: 0;
}

.gallery-title {
  font-family: "brown-sugar";
  font-size: 50px;
  line-height: 1;
  margin-bottom: 40px;
  opacity: 0;
  transform: translateY(24px);
  transition: opacity 0.8s ease 0.2s, transform 0.8s cubic-bezier(0.22, 1, 0.36, 1) 0.2s;
}

.gallery-title.is-ready {
  opacity: 1;
  transform: translateY(0);
}

/* Masonry: photos are dealt into flex columns so each keeps its own shape */
.gallery-grid {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.gallery-column {
  flex: 1;
  min-width: 0;
}

.gallery-item {
  margin: 0 0 16px;
  background-color: var(--color-paper);
  opacity: 0;
  transform: translateY(32px);
  transition: opacity 0.8s ease, transform 0.8s cubic-bezier(0.22, 1, 0.36, 1);
  transition-delay: calc(0.35s + var(--delay));
}

.gallery-grid.is-ready .gallery-item {
  opacity: 1;
  transform: translateY(0);
}

/* width/height attributes reserve each photo's space; it fades in once downloaded */
.gallery-item img {
  display: block;
  width: 100%;
  height: auto;
  opacity: 0;
  transition: opacity 0.6s ease;
}

.gallery-item img.is-loaded {
  opacity: 1;
}

@media (max-width: 899px) {
  .gallery-page {
    padding: 40px 20px 96px;
  }

  .gallery-title {
    font-size: 40px;
    margin-bottom: 28px;
  }

  .gallery-grid {
    gap: 12px;
  }

  .gallery-item {
    margin-bottom: 12px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .gallery-title,
  .gallery-item {
    transform: none;
    transition: opacity 0.4s ease;
  }
}
</style>
